import { AxiosResponse } from "axios";
import { api } from "../api";
import {
  SchoolEntity,
  SchoolEntityInput,
  SchoolUserEntity,
  CreateSchoolUserDto,
  UpdateSchoolUserDto,
  BulkCreateUsersDto,
  BulkCreateResponse,
  BulkValidateUsersDto,
  BulkValidationResponse,
  SchoolUserPaginatedResponse,
  RegenerateUsernamesResponse,
} from "./school.types";
import {
  CampusScopeResult,
  CampusSummary,
  CreateCampusInput,
  MoveUserResult,
  UpdateCampusInput,
} from "./campus.types";

export const getSchools = async () => {
  try {
    const response: AxiosResponse<SchoolEntity[]> = await api.get("/schools");
    return response.status === 200 ? response.data : [];
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const getSchoolById = async (id: number) => {
  try {
    const response: AxiosResponse<SchoolEntity> = await api.get(
      "/schools/" + id
    );
    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const createSchool = async (data: SchoolEntityInput) => {
  try {
    const response: AxiosResponse<SchoolEntity> = await api.post(
      "/schools",
      data
    );

    return response.status === 201 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const updateSchool = async (id: number, data: SchoolEntityInput) => {
  try {
    const response: AxiosResponse<SchoolEntity> = await api.patch(
      "/schools/" + id,
      data
    );

    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const updateSchoolGroups = async (id: number, groups: string[]) => {
  try {
    const response: AxiosResponse<SchoolEntity> = await api.patch(
      "/schools/" + id,
      { groups }
    );

    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const addGroupToSchool = async (schoolId: number, groupName: string) => {
  try {
    const response = await api.post(`/schools/${schoolId}/groups`, {
      groupName
    });
    return response.status === 201 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const removeGroupFromSchool = async (schoolId: number, groupName: string) => {
  try {
    const response = await api.delete(`/schools/${schoolId}/groups/${groupName}`);
    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const editGroupInSchool = async (schoolId: number, currentGroupName: string, newGroupName: string) => {
  try {
    const response = await api.patch(`/schools/${schoolId}/groups/edit`, {
      currentGroupName,
      newGroupName
    });
    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

export const deleteSchool = async (id: number) => {
  try {
    const response: AxiosResponse<SchoolEntity> = await api.delete(
      "/schools/" + id
    );

    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(error);
    throw error;
  }
};

// ========== FUNCIONES PARA USUARIOS DE ESCUELA ==========

export const createSchoolUser = async (
  data: CreateSchoolUserDto
): Promise<SchoolUserEntity | null> => {
  try {
    const response: AxiosResponse<SchoolUserEntity> = await api.post(
      "/schools/users",
      data
    );
    return response.status === 201 ? response.data : null;
  } catch (error: any) {
    console.error("Error creating school user:", error);
    throw error;
  }
};

// La función getSchoolUsersBySchoolId ya soporta el argumento filters y classroom_id
export const getSchoolUsersBySchoolId = async (
  schoolId: number,
  page: number = 1,
  pageSize: number = 30,
  filters: any = {} // <-- nuevo argumento
): Promise<SchoolUserPaginatedResponse> => {
  try {
    const params = { page, pageSize, ...filters };
    const response: AxiosResponse<SchoolUserPaginatedResponse> = await api.get(
      `/schools/${schoolId}/users`,
      { params }
    );
    return response.status === 200
      ? response.data
      : { data: [], total: 0, page, pageSize };
  } catch (error: any) {
    console.error(`Error fetching users for school ${schoolId}:`, error);
    throw error;
  }
};

export const getSchoolUserById = async (
  id: number
): Promise<SchoolUserEntity | null> => {
  try {
    const response: AxiosResponse<SchoolUserEntity> = await api.get(
      `/schools/users/${id}`
    );
    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(`Error fetching school user ${id}:`, error);
    throw error;
  }
};

export const updateSchoolUser = async (
  id: number,
  data: UpdateSchoolUserDto
): Promise<SchoolUserEntity | null> => {
  try {
    const response: AxiosResponse<SchoolUserEntity> = await api.patch(
      `/schools/users/${id}`,
      data
    );
    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error(`Error updating school user ${id}:`, error);
    throw error;
  }
};

export const deleteSchoolUser = async (id: number): Promise<void> => {
  try {
    await api.delete(`/schools/users/${id}`);
  } catch (error: any) {
    console.error(`Error deleting school user ${id}:`, error);
    throw error;
  }
};

export const bulkValidateSchoolUsers = async (
  data: BulkValidateUsersDto
): Promise<BulkValidationResponse | null> => {
  try {
    const response: AxiosResponse<BulkValidationResponse> = await api.post(
      "/schools/users/bulk/validate",
      data
    );

    // Retornar los datos independientemente del status code si hay data
    if (response.data) {
      return response.data;
    } else {
      return null;
    }
  } catch (error: any) {
    // Si el error tiene una respuesta con datos válidos, intentar usarla
    if (error.response?.data && error.response.data.hasConflicts !== undefined) {
      console.log("🔧 Recuperando datos del error response");
      return error.response.data;
    }

    throw error;
  }
};

export const bulkCreateSchoolUsers = async (
  data: BulkCreateUsersDto
): Promise<BulkCreateResponse | null> => {
  try {
    const response: AxiosResponse<BulkCreateResponse> = await api.post(
      "/schools/users/bulk",
      data
    );
    return response.status === 201 ? response.data : null;
  } catch (error: any) {
    console.error("Error bulk creating school users:", error);
    throw error;
  }
};

export const resendBulkTeacherInvitations = async (userIds: number[]) => {
  try {
    const response: AxiosResponse<{
      success: boolean;
      message: string;
      requested: number;
      valid_teachers: number;
      sent: number;
      failed: number;
      errors: Array<{ userId: number; email: string; error: string }>;
    }> = await api.post("/schools/users/resend-invitations/bulk", {
      user_ids: userIds,
    });

    return response.status === 200 || response.status === 201 
      ? response.data 
      : null;
  } catch (error: any) {
    console.error("Error resending bulk teacher invitations:", error);
    throw error;
  }
};

export const resendBulkStudentWelcomes = async (userIds: number[]) => {
  try {
    const response: AxiosResponse<{
      success: boolean;
      message: string;
      requested: number;
      valid_students: number;
      sent: number;
      failed: number;
      errors: Array<{ userId: number; email: string; error: string }>;
    }> = await api.post("/schools/users/resend-welcomes/bulk", {
      user_ids: userIds,
    });

    return response.status === 200 || response.status === 201
      ? response.data
      : null;
  } catch (error: any) {
    console.error("Error resending bulk student welcomes:", error);
    throw error;
  }
};

export const regenerateStudentUsernames = async (
  schoolId: number,
  dryRun = false,
  abbreviation?: string
): Promise<RegenerateUsernamesResponse | null> => {
  try {
    const response: AxiosResponse<RegenerateUsernamesResponse> = await api.post(
      "/school-users/students/regenerate-usernames",
      {},
      {
        params: {
          schoolId,
          ...(dryRun ? { dryRun: true } : {}),
          ...(dryRun && abbreviation ? { abbreviation } : {}),
        },
      }
    );
    return response.status === 200 || response.status === 201
      ? response.data
      : null;
  } catch (error: any) {
    console.error("Error regenerating student usernames:", error);
    throw error;
  }
};

// ========== PLANTELES (SCRUM-916) ==========

export const getCampuses = async (schoolId: number) => {
  try {
    const response: AxiosResponse<CampusSummary[]> = await api.get(
      `/schools/${schoolId}/campuses`
    );
    return response.status === 200 ? response.data : [];
  } catch (error: any) {
    console.error("Error fetching campuses:", error);
    throw error;
  }
};

export const createCampus = async (
  schoolId: number,
  data: CreateCampusInput
) => {
  try {
    const response: AxiosResponse<SchoolEntity> = await api.post(
      `/schools/${schoolId}/campuses`,
      data
    );
    return response.status === 201 || response.status === 200
      ? response.data
      : null;
  } catch (error: any) {
    console.error("Error creating campus:", error);
    throw error;
  }
};

export const updateCampus = async (
  schoolId: number,
  campusId: number,
  data: UpdateCampusInput
) => {
  try {
    const response: AxiosResponse<SchoolEntity> = await api.patch(
      `/schools/${schoolId}/campuses/${campusId}`,
      data
    );
    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error("Error updating campus:", error);
    throw error;
  }
};

export const moveUserToCampus = async (
  userId: number,
  targetSchoolId: number
) => {
  try {
    const response: AxiosResponse<MoveUserResult> = await api.post(
      `/schools/users/${userId}/move`,
      { targetSchoolId }
    );
    return response.status === 200 || response.status === 201
      ? response.data
      : null;
  } catch (error: any) {
    console.error("Error moving user to campus:", error);
    throw error;
  }
};

export const setCoordinatorCampusScope = async (
  userId: number,
  schoolIds: number[]
) => {
  try {
    const response: AxiosResponse<CampusScopeResult> = await api.put(
      `/schools/users/${userId}/campus-scope`,
      { schoolIds }
    );
    return response.status === 200 ? response.data : null;
  } catch (error: any) {
    console.error("Error setting coordinator campus scope:", error);
    throw error;
  }
};
